import React, { useState, useEffect } from 'react';

/**
 * A reusable "dropdown" style multi-select component using checkboxes.
 * Allows multiple selections without the usual Ctrl+click.
 * An option may set `disabled` (listed but can't be added) and `hint` (muted
 * text shown beside its label).
 */
export const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange,
  required = false,
  showValidation = false,
  // While true, a selected value with no matching option renders a spinner
  // chip instead of its raw value — the option list is still being fetched
  // and a bare id must never flash on screen.
  resolvingLabels = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (value) => {
    let newSelected;
    if (selectedValues.includes(value)) {
      newSelected = selectedValues.filter((v) => v !== value);
    } else {
      newSelected = [...selectedValues, value];
    }
    onChange(newSelected);
  };

  const handleOutsideClick = (e) => {
    // Close dropdown if clicked outside
    if (!e.target.closest('.msd-wrapper')) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    } else {
      document.removeEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  return (
    <div className="msd-wrapper relative w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div
        className={`flex flex-wrap items-center gap-1.5 w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-colors ${
          showValidation && required && selectedValues.length < 1
            ? 'border-red-500'
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedValues.length > 0 ? (
          <>
            {selectedValues.map((val) => {
              const opt = options.find(o => o.value === val);
              if (!opt && resolvingLabels) {
                return (
                  <span
                    key={val}
                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-100 dark:bg-primary-900/40 border border-primary-200 dark:border-primary-700"
                  >
                    <span
                      className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"
                      role="status"
                      aria-label="Loading name"
                    />
                  </span>
                );
              }
              return (
                <span
                  key={val}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
                >
                  {opt?.label ?? val}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    className="hover:text-primary-900 dark:hover:text-primary-100 leading-none"
                    aria-label={`Remove ${opt?.label ?? val}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">Select...</span>
        )}
        <svg
          className={`w-4 h-4 text-gray-400 dark:text-gray-500 transform transition-transform flex-shrink-0 ml-auto ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900/20 max-h-60 overflow-auto min-w-full" style={{ width: 'max-content', minWidth: '100%', maxWidth: '400px' }}>
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center whitespace-nowrap">No options available</div>
          ) : (
            options.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);
              // A disabled option can't be added, but one that is already
              // selected stays uncheckable so the user can clear it.
              const locked = !!opt.disabled && !isSelected;
              return (
                <label
                  key={opt.value}
                  className={`flex items-center px-4 py-3 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg whitespace-nowrap ${
                    locked ? 'cursor-not-allowed opacity-50' : 'hover:bg-primary-50 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 focus:ring-2 flex-shrink-0"
                    checked={isSelected}
                    disabled={locked}
                    onChange={() => { if (!locked) toggleOption(opt.value); }}
                  />
                  <span className="ml-4 text-sm text-gray-700 dark:text-gray-200 select-none leading-5">{opt.label}</span>
                  {opt.hint && (
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 select-none">{opt.hint}</span>
                  )}
                </label>
              );
            })
          )}
        </div>
      )}
      {showValidation && required && selectedValues.length < 1 && (
        <p className="mt-1 text-sm text-red-500">Please select one entry.</p>
      )}
    </div>
  );
};

/**
 * A single select dropdown component that uses radio buttons.
 * Only allows one selection at a time.
 */
export const SingleSelectDropdown = ({
  label,
  options,
  selectedValue,
  onChange,
  required = false,
  showValidation = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectOption = (value) => {
    onChange(value);
    setIsOpen(false);
  };

  const handleOutsideClick = (e) => {
    // Close dropdown if clicked outside
    if (!e.target.closest('.ssd-wrapper')) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    } else {
      document.removeEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  // Find the selected option to display its label
  const selectedOption = options.find(opt => opt.value === selectedValue);
  
  return (
    <div className="ssd-wrapper relative w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <button
        type="button"
        className={`inline-flex items-center justify-between w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
          showValidation && required && !selectedValue
            ? 'border-red-500'
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
          {selectedValue
            ? selectedOption?.label || 'Selected'
            : 'Select...'}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 dark:text-gray-500 transform transition-transform flex-shrink-0 ml-2 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900/20 max-h-60 overflow-auto min-w-full" style={{ width: 'max-content', minWidth: '100%', maxWidth: '400px' }}>
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center whitespace-nowrap">No options available</div>
          ) : (
            options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center px-4 py-3 hover:bg-primary-50 cursor-pointer transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg whitespace-nowrap"
              >
                <input
                  type="radio"
                  className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 focus:ring-2 flex-shrink-0"
                  checked={selectedValue === opt.value}
                  onChange={() => selectOption(opt.value)}
                />
                <span className="ml-4 text-sm text-gray-700 dark:text-gray-200 select-none leading-5">{opt.label}</span>
              </label>
            ))
          )}
        </div>
      )}
      {showValidation && required && !selectedValue && (
        <p className="mt-1 text-sm text-red-500">Please select an option.</p>
      )}
    </div>
  );
};

export default {
  MultiSelectDropdown,
  SingleSelectDropdown
};
